import React, { Component } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { Link } from "react-router-dom";

import {
  RetailPosModule,
  InventoryModule,
  PurchasesModule,
  OrdersManagementModule,
  DeliveryManagementModule,
  PackagingManagementModule,
  CrmModule,
  HrmModule,
  AccountingModule,
  ReportsModule,
  LeadsManagementModule,
  CouponsManagementModule,
  ECommerceB2CModule,
  ECommerceB2BModule,
  ProductionModule,
  RawMaterialsModule,
  DemoButton,
  DigitalCatalogModule,
  QuoteManagementModule,
  SalesOrderModule,
} from "./CommonModules";

import CartContext from "./CartContext";

/**
 * Production (Manufacturing-focused) — class component
 * - Preserves existing context usage and Try/Buy flows
 * - Focused hero, features and module grid tailored to manufacturing/MRP/BOM/work-orders/shop-floor
 */
export class Production extends Component {
  static contextType = CartContext;

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  handleTryNow = () => {
    const {
      userData,
      ShowUserLoginNew,
      SetSelectedProduct,
      prods,
      ProductType,
    } = this.context;

    if (!userData || userData.loginStatus !== "active") {
      if (typeof ShowUserLoginNew === "function") ShowUserLoginNew(true);
      return;
    }

    if (Array.isArray(prods) && ProductType) {
      const selected = prods.find((p) => p.productType === ProductType.ERP);
      if (selected && typeof SetSelectedProduct === "function") {
        SetSelectedProduct(selected);
      }
    }
  };

  render() {
    if (
      !this.context ||
      !this.context.EcSettings ||
      this.context.EcSettings.length === 0
    ) {
      return <></>;
    }

    const base = this.context.GetEcSettingValueByName
      ? this.context.GetEcSettingValueByName("StorageBlobImageUrl")
      : "";

    return (
      <>
        {/* Hero / Manufacturing Marketing Section */}
        <section
          className="hero container-fluid py-5 bg-white"
          id="productionSection"
        >
          <div className="container">
            <div className="row align-items-center gy-4">
              <div className="col-lg-7">
                <span className="badge-sub">
                  Manufacturing • Production Management
                </span>

                <h1 className="display-5 fw-bold mt-3">
                  Manufacturing ERP — MRP, BOM, Work Orders & Shop-floor Control
                </h1>

                <p className="lead text-muted mb-4">
                  A manufacturing-first ERP that connects planning, procurement,
                  production and fulfillment. Manage Bills of Materials, run
                  Material Requirements Planning, schedule work orders and
                  monitor shop-floor execution — all with traceability and cost
                  control.
                </p>

                <p className="mb-4">
                  <strong>Why choose ElasticCART for Manufacturing?</strong>
                </p>

                <div className="row g-3">
                  <div className="col-sm-6">
                    <div className="d-flex gap-3 align-items-start">
                      <div className="feature-icon">
                        <i className="fa-solid fa-boxes-stacked" />
                      </div>
                      <div>
                        <h6 className="mb-1">BOM & Routing</h6>
                        <small className="text-muted">
                          Multi-level BOMs, routings and version control.
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="d-flex gap-3 align-items-start">
                      <div className="feature-icon">
                        <i className="fa-solid fa-calculator" />
                      </div>
                      <div>
                        <h6 className="mb-1">MRP & Demand Planning</h6>
                        <small className="text-muted">
                          Auto-generate purchase suggestions & production plans.
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="d-flex gap-3 align-items-start">
                      <div className="feature-icon">
                        <i className="fa-solid fa-industry" />
                      </div>
                      <div>
                        <h6 className="mb-1">Work Orders & Scheduling</h6>
                        <small className="text-muted">
                          Create, schedule and track WIP with capacity checks.
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="d-flex gap-3 align-items-start">
                      <div className="feature-icon">
                        <i className="fa-solid fa-tools" />
                      </div>
                      <div>
                        <h6 className="mb-1">Shop-floor & Quality</h6>
                        <small className="text-muted">
                          Operator terminals, QA checks, lot/serial
                          traceability.
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  {/* <Button
                    variant="light"
                    size="lg"
                    className="me-2"
                    onClick={this.handleTryNow}
                  >
                    <i className="fa-solid fa-play me-2" /> Book a demo
                    +91-98805 82728
                  </Button> */}

                  <Button
                    variant="outline-primary"
                    size="lg"
                    as={Link}
                    to="/Checkout"
                    onClick={() =>
                      this.context.HandleTryNowButtonClick(
                        this.context.ProductType.ProductionManagement
                      )
                    }
                  >
                    <i className="fa-solid fa-flask me-2" /> Start free trial
                  </Button>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="card card-feature p-4">
                  <div className="card-body">
                    <h5 className="card-title mb-3">
                      Key manufacturing capabilities
                    </h5>
                    <div className="row">
                      <div className="col-6 mb-3">
                        <i className="fa-solid fa-sitemap fa-lg me-2" />
                        <strong>BOM Management</strong>
                        <div className="text-muted small">
                          Configurable multi-level BOMs & revisions.
                        </div>
                      </div>
                      <div className="col-6 mb-3">
                        <i className="fa-solid fa-calendar-days fa-lg me-2" />
                        <strong>MRP & Scheduling</strong>
                        <div className="text-muted small">
                          Material planning and production scheduling.
                        </div>
                      </div>
                      <div className="col-6 mb-3">
                        <i className="fa-solid fa-warehouse fa-lg me-2" />
                        <strong>Inventory & Traceability</strong>
                        <div className="text-muted small">
                          Lot/serial tracking, WIP and warehouse integration.
                        </div>
                      </div>
                      <div className="col-6 mb-3">
                        <i className="fa-solid fa-user-gear fa-lg me-2" />
                        <strong>Shop-floor</strong>
                        <div className="text-muted small">
                          Operator terminals, time & resource capture.
                        </div>
                      </div>
                      <div className="col-6 mb-3">
                        <i className="fa-solid fa-check-to-slot fa-lg me-2" />
                        <strong>Quality</strong>
                        <div className="text-muted small">
                          Inspections, NCRs and corrective actions.
                        </div>
                      </div>
                      <div className="col-6 mb-3">
                        <i className="fa-solid fa-scale-balanced fa-lg me-2" />
                        <strong>Costing & Reporting</strong>
                        <div className="text-muted small">
                          Standard & actual costing, production KPIs.
                        </div>
                      </div>
                    </div>

                    <div className="mt-3">
                      {/* <Button
                        variant="primary"
                        size="sm"
                        as={Link}
                        to="#features"
                      >
                        See full feature list
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        as={Link}
                        to="#contact"
                        className="ms-2"
                      >
                        Contact sales
                      </Button> */}
                      <DemoButton></DemoButton>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product overview text */}
              <div className="col-12 mt-4">
                <div>
                  Built for discrete and process manufacturers, ElasticCART
                  Manufacturing connects planning, procurement, production and
                  quality — reducing lead times, improving yields and giving you
                  full traceability from raw material to finished goods.
                </div>
              </div>

              {/* Demo Video */}
              <div className="col-lg-12 mb-lg-5 py-5">
                <video
                  controls
                  height="400px"
                  autoPlay
                  muted
                  loop
                  width="100%"
                  aria-label="Manufacturing demo video"
                >
                  <source
                    src={base + "ManufacturingErpVideo.mp4"}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            {/* Modules short list */}
            <div className="row mt-5 modules">
              <div className="col-lg-8">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <h4 className="mb-3">What we cover (Manufacturing)</h4>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <ul className="list-unstyled">
                          <li>
                            <strong>BOM & Routings:</strong> multi-level BOMs,
                            alternate BOMs, routings and work centers.
                          </li>
                          <li>
                            <strong>MRP & Demand Planning:</strong> netting,
                            forecasts, safety stock & auto PO suggestions.
                          </li>
                          <li>
                            <strong>Work Orders & WIP:</strong> create WOs,
                            track progress, scrap and WIP costs.
                          </li>
                          <li>
                            <strong>Shop-floor Execution:</strong> terminals,
                            barcode scanning and operator input.
                          </li>
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <ul className="list-unstyled">
                          <li>
                            <strong>Quality & Traceability:</strong> QC checks,
                            lot & serial traceability and CAPA.
                          </li>
                          <li>
                            <strong>Maintenance & Resources:</strong> preventive
                            maintenance, tool/resource allocation.
                          </li>
                          <li>
                            <strong>Costing & Analytics:</strong>{" "}
                            standard/actual cost, variance and production KPIs.
                          </li>
                          <li>
                            <strong>Integration:</strong> inventory, purchasing,
                            sales and financials for end-to-end flow.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="cta-strip text-center p-4 rounded">
                  <h5 className="mb-2">Start optimizing production</h5>
                  <p className="mb-3 small text-muted">
                    Book a demo and see how ElasticCART reduces lead-time,
                    improves OEE and increases throughput.
                  </p>
                  {/* <Button
                    variant="light"
                    size="sm"
                    as={Link}
                    to="#demo"
                    className="me-2"
                    onClick={this.handleTryNow}
                  >
                    <i className="fa-solid fa-calendar-check me-2" /> Book a
                    demo
                  </Button> */}
                  {/* <Button
                    variant="outline-light"
                    size="sm"
                    as={Link}
                    to="/Checkout"
                    onClick={() =>
                      this.context.HandleTryNowButtonClick(
                        this.context.ProductType.ProductionManagement
                      )
                    }
                  >
                    <i className="fa-regular fa-clock me-2" /> Start free trial
                  </Button> */}
                  <DemoButton></DemoButton>
                </div>

                <div className="card mt-3 border-0">
                  <div className="card-body small text-muted">
                    <strong>Trusted by:</strong>
                    <div className="mt-2 d-flex gap-2 flex-wrap">
                      <span className="badge bg-light text-dark">
                        Manufacturing
                      </span>
                      <span className="badge bg-light text-dark">
                        Automotive
                      </span>
                      <span className="badge bg-light text-dark">FMCG</span>
                      <span className="badge bg-light text-dark">Pharma</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer CTA */}
            <div className="row mt-5">
              <div className="col-12 text-center">
                <p className="text-muted small mb-2">
                  See ElasticCART Manufacturing in action — 30-minute demo, no
                  obligation.
                </p>
                {/* <Button
                  variant="outline-primary"
                  size="lg"
                  className="me-2"
                  onClick={this.handleTryNow}
                >
                  Book a demo +91-98805 82728
                </Button>
                <Button variant="primary" size="lg" as={Link} to="#contact">
                  Contact sales
                </Button> */}
                <DemoButton></DemoButton>
              </div>
            </div>
          </div>
        </section>

        {/* Manufacturing Modules Section */}
        <section className="container my-5">
          <h5 className="display-6 text-dark mb-4 border-start border-5 border-primary ps-3 py-5">
            Explore Manufacturing Modules That Drive Production Excellence
          </h5>
          <Row className="g-3">
            {[
              RawMaterialsModule,
              PurchasesModule,
              InventoryModule,
              ProductionModule,
              PackagingManagementModule,
              OrdersManagementModule,
              DeliveryManagementModule,
              DigitalCatalogModule,
              LeadsManagementModule,
              QuoteManagementModule,
              SalesOrderModule,
              RetailPosModule,
              ReportsModule,
              AccountingModule,
              HrmModule,
            ].map((Module, index) => (
              <Col md={6} lg={6} key={index}>
                <div className="bg-light p-3 rounded shadow-sm h-100 hover-shadow">
                  {Module ? <Module /> : <div className="p-3">Module</div>}
                </div>
              </Col>
            ))}
          </Row>
        </section>
      </>
    );
  }
}

// Reusable FeatureSection remains available if you want to reuse elsewhere
const FeatureSection = ({ title, items }) => (
  <div className="mb-4">
    <Row>
      {items.map((item, idx) => (
        <Col md={6} key={idx}>
          <li className="mb-2">{item}</li>
        </Col>
      ))}
    </Row>
  </div>
);

export default Production;
